import RestartAltIcon from '@mui/icons-material/RestartAlt'
import StopIcon from '@mui/icons-material/Stop'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'

import {
  LamictalScheduleContext,
  LamictalScheduleContextInterface,
  LamictalScheduleObj,
} from '../../../providers/PatientChartPage/LamictalSchedule.provider'
import { themeColors } from '../../../theme'
import { useStyles } from '../PatientChartPage.styles'

interface LamictalScheduleReviewProps {
  patientId: string | undefined
}

const DOSAGE_STEPS = [25, 50, 100, 150, 200]

export const LamictalScheduleReview = ({
  patientId,
}: LamictalScheduleReviewProps) => {
  const { classes } = useStyles()

  const {
    lamictalSchedules,
    activeSchedule,
    getPatientLamictalSchedules,
    startSchedule,
    resetDosage,
    terminateSchedule,
  } = useContext(LamictalScheduleContext) as LamictalScheduleContextInterface

  const [actionLoading, setActionLoading] = useState(false)

  // Reset dialog
  const [resetDialogOpen, setResetDialogOpen] = useState(false)
  const [resetDosageValue, setResetDosageValue] = useState<number>(25)

  // Terminate dialog
  const [terminateDialogOpen, setTerminateDialogOpen] = useState(false)

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({ open: false, message: '', severity: 'success' })

  useEffect(() => {
    if (patientId) {
      getPatientLamictalSchedules(Number(patientId))
    }
  }, [patientId])

  const handleStart = async () => {
    try {
      setActionLoading(true)
      await startSchedule(Number(patientId))
      showSnackbar('Lamictal schedule started at 25mg', 'success')
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to start schedule'
      showSnackbar(msg, 'error')
    } finally {
      setActionLoading(false)
    }
  }

  const handleReset = async () => {
    if (!activeSchedule) return
    try {
      setActionLoading(true)
      await resetDosage(activeSchedule.id, resetDosageValue)
      showSnackbar(`Dosage reset to ${resetDosageValue}mg`, 'success')
      setResetDialogOpen(false)
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to reset dosage'
      showSnackbar(msg, 'error')
    } finally {
      setActionLoading(false)
    }
  }

  const handleTerminate = async () => {
    if (!activeSchedule) return
    try {
      setActionLoading(true)
      await terminateSchedule(activeSchedule.id)
      showSnackbar('Lamictal schedule titration discontinue', 'success')
      setTerminateDialogOpen(false)
    } catch (err: any) {
      showSnackbar('Failed to discontinue schedule titration', 'error')
    } finally {
      setActionLoading(false)
    }
  }

  //  Helpers

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity })
  }

  const getActiveStepIndex = (dosageMg: number) => {
    const idx = DOSAGE_STEPS.indexOf(dosageMg)
    return idx >= 0 ? idx : 0
  }

  const resetOptions = activeSchedule
    ? DOSAGE_STEPS.filter((d) => d !== activeSchedule.currentDosageMg)
    : DOSAGE_STEPS

  const renderPastActionLabel = (log: {
    action: string
    previousDosageMg: number
    newDosageMg: number
  }) => {
    switch (log.action) {
      case 'Start':
        return `Started at ${log.newDosageMg}mg`
      case 'Increase':
        return `${log.previousDosageMg}mg → ${log.newDosageMg}mg`
      case 'Reset':
        return `Reset from ${log.previousDosageMg}mg → ${log.newDosageMg}mg`
      case 'Terminate':
        return `Titration Discontinued at ${log.previousDosageMg}mg`
      default:
        return log.action
    }
  }

  const pastSchedules = lamictalSchedules.filter((s) => !s.isActive)

  const renderSchedule = (schedule: LamictalScheduleObj, isActive: boolean) => (
    <Box key={schedule.id} style={{ marginBottom: '50px' }}>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          margin: '15px 0',
        }}
      >
        <Typography fontWeight={700} mr={2}>
          {isActive
            ? 'Ongoing Lamictal Schedule'
            : `Past Schedule — ${dayjs(schedule.startedAt).format(
                'MM/DD/YYYY'
              )}`}
        </Typography>

        {isActive && (
          <Chip
            label={schedule.isAtCeiling ? 'At Ceiling (200mg)' : 'Active'}
            color={schedule.isAtCeiling ? 'success' : 'primary'}
            size='small'
          />
        )}

        {!isActive && (
          <Chip
            label='Titration Discontinued'
            color='error'
            size='small'
            variant='outlined'
          />
        )}
      </Box>

      {/* Dosage stepper */}
      <Stepper
        activeStep={getActiveStepIndex(schedule.currentDosageMg)}
        alternativeLabel
        style={{ margin: '20px 0' }}
      >
        {DOSAGE_STEPS.map((dose) => (
          <Step key={dose} completed={dose <= schedule.currentDosageMg}>
            <StepLabel>{dose}mg</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Schedule details */}
      <Box style={{ margin: '15px 0' }}>
        <Typography variant='body2' color='textSecondary'>
          Started on: {dayjs(schedule.startedAt).format('YYYY-MM-DD hh:mm A')}
        </Typography>

        <Typography
          variant='body2'
          color='textSecondary'
          style={{ marginTop: '4px' }}
        >
          Current dosage: <strong>{schedule.currentDosageMg}mg</strong>
        </Typography>

        {schedule.nextDosageMg && schedule.nextIncreaseAt && (
          <Typography
            variant='body2'
            color='textSecondary'
            style={{ marginTop: '4px' }}
          >
            Next increase to <strong>{schedule.nextDosageMg}mg</strong> on{' '}
            {dayjs(schedule.nextIncreaseAt).format('MM/DD/YYYY')}
          </Typography>
        )}

        {schedule.isAtCeiling && (
          <Typography
            variant='body2'
            style={{
              marginTop: '4px',
              color: themeColors.green || 'green',
            }}
          >
            Ceiling reached — no further increases scheduled.
          </Typography>
        )}

        {!schedule.isActive && schedule.terminatedAt && (
          <Typography
            variant='body2'
            style={{ marginTop: '4px', color: themeColors.red }}
          >
            Titration discontinued on{' '}
            {dayjs(schedule.terminatedAt).format('MM/DD/YYYY')}
          </Typography>
        )}
      </Box>

      {/* Action buttons — only for active */}
      {isActive && (
        <Box
          style={{
            display: 'flex',
            gap: '10px',
            margin: '20px 0',
          }}
        >
          <Button
            variant='contained'
            onClick={() => {
              setResetDosageValue(resetOptions[0] ?? 25)
              setResetDialogOpen(true)
            }}
            disabled={actionLoading}
            style={{ fontSize: '14px' }}
            startIcon={<RestartAltIcon />}
          >
            Reset Dosage
          </Button>

          <Button
            variant='contained'
            onClick={() => setTerminateDialogOpen(true)}
            disabled={actionLoading}
            style={{
              fontSize: '14px',
              backgroundColor: themeColors.red,
              borderColor: 'transparent',
            }}
            startIcon={<StopIcon />}
          >
            Discontinue Titration
          </Button>
        </Box>
      )}

      {/* History log table */}
      {schedule.logs.length > 0 && (
        <table className={classes.reviewTable}>
          <thead className={classes.tableThead}>
            <tr>
              <th className={classes.tableLabelTh} scope='col'>
                Action Details
              </th>
              <th className={classes.tableLabelTh} scope='col'>
                Date
              </th>
            </tr>
          </thead>
          <tbody>
            {schedule.logs.map((log, i) => (
              <tr key={`lamictal-log-${schedule.id}-${i}`}>
                <th className={classes.tableTh} scope='col'>
                  {renderPastActionLabel(log)}
                </th>
                <th className={classes.tableTh} scope='col'>
                  {dayjs(log.occurredAt).format('MM/DD/YYYY')}
                </th>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Box>
  )

  return (
    <Box>
      {/* Start button — only when no active schedule */}
      {!activeSchedule && (
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '30px 0',
          }}
        >
          <Typography style={{ marginBottom: '15px' }} color='textSecondary'>
            No active Lamictal schedule for this patient.
          </Typography>
          <Button
            variant='contained'
            onClick={handleStart}
            disabled={actionLoading}
            style={{ fontSize: '16px', width: '270px' }}
          >
            {actionLoading ? (
              <CircularProgress size={20} color='primary' />
            ) : (
              'Start Lamictal Schedule'
            )}
          </Button>
        </Box>
      )}

      {/* Active schedule */}
      {activeSchedule && renderSchedule(activeSchedule, true)}

      {pastSchedules.length > 0 && (
        <Divider
          sx={{
            my: 4,
            border: `1px dashed ${themeColors.red}`,
          }}
        />
      )}

      {/* Past schedules */}
      {pastSchedules.length > 0 && (
        <>
          {/* <Typography fontWeight={700} style={{ margin: '25px 0 15px 0' }}>
            Past Schedules
          </Typography> */}
          {pastSchedules.map((s) => renderSchedule(s, false))}
        </>
      )}

      {/* Reset Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth='xs'
        fullWidth
      >
        <DialogTitle>Reset Lamictal Dosage</DialogTitle>
        <DialogContent>
          <DialogContentText style={{ marginBottom: '16px' }}>
            Choose the dosage to reset to. The 2-week timer will restart from
            this level.
          </DialogContentText>

          <FormControl fullWidth size='small'>
            <InputLabel>Dosage</InputLabel>
            <Select
              value={resetDosageValue}
              label='Dosage'
              onChange={(e: SelectChangeEvent<number>) =>
                setResetDosageValue(Number(e.target.value))
              }
            >
              {resetOptions.map((dose) => (
                <MenuItem key={dose} value={dose}>
                  {dose}mg
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setResetDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleReset}
            variant='contained'
            disabled={actionLoading}
          >
            {actionLoading ? (
              <CircularProgress size={20} color='primary' />
            ) : (
              `Reset to ${resetDosageValue}mg`
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Terminate(Discontinue Titration) Dialog */}
      <Dialog
        open={terminateDialogOpen}
        onClose={() => setTerminateDialogOpen(false)}
      >
        <DialogTitle>Discontinue Lamictal Titration</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will stop all future dosage increases and notifications for
            this patient. Are you sure?
          </DialogContentText>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setTerminateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleTerminate}
            variant='contained'
            disabled={actionLoading}
            style={{ backgroundColor: themeColors.red }}
          >
            {actionLoading ? (
              <CircularProgress size={20} color='primary' />
            ) : (
              'Discontinue Titration'
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          variant='filled'
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}
